import React, { useEffect, useRef, useState } from "react";
import { axios } from "../../../../config";
import { taskEndpoints } from "../../../../utils/constants/httpConstants";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import { EMITTER_ERROR } from "../../../../utils/constants";
import { GREY, BORDER, BASE, HELP } from "../../../../utils/constants/colors";

// Compact call-recording player. Fetches the recording through the authenticated
// axios path (blob -> object URL) so it reaches the API gateway, then renders a
// real play/pause + draggable seek bar (native <audio controls> can't seek a
// streamed mp3 whose duration metadata is missing).
const fmt = (s) => {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${sec < 10 ? "0" : ""}${sec}`;
};

const CallRecordingPlayer = ({ recordingId, providerCallId }) => {
  const audioRef = useRef(null);
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [cur, setCur] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);

  const load = async () => {
    setLoading(true);
    try {
      const q = recordingId
        ? `?recId=${encodeURIComponent(recordingId)}`
        : `?sessionId=${encodeURIComponent(providerCallId)}`;
      const res = await axios.get(taskEndpoints.voiceRecording + q, { responseType: "blob" });
      setUrl(URL.createObjectURL(res.data));
    } catch (e) {
      EventEmitter.emit(EMITTER_ERROR, "Recording not ready yet — try again in a moment");
    } finally {
      setLoading(false);
    }
  };

  // Streamed mp3s often report duration = Infinity until fully scanned. Nudge the
  // browser to compute the real duration so the seek bar works.
  const onLoadedMeta = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.duration === Infinity || isNaN(a.duration)) {
      const fix = () => {
        a.removeEventListener("timeupdate", fix);
        a.currentTime = 0;
        setDur(a.duration);
      };
      a.addEventListener("timeupdate", fix);
      a.currentTime = 1e101;
    } else {
      setDur(a.duration);
    }
  };

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) a.play(); else a.pause();
  };
  const onSeek = (e) => {
    const a = audioRef.current;
    const v = Number(e.target.value);
    if (a) a.currentTime = v;
    setCur(v);
  };

  if (!url) {
    return (
      <div
        onClick={load}
        title="Play recording"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: "8px",
          cursor: loading ? "default" : "pointer",
          width: "fit-content",
          padding: "7px 14px 7px 8px",
          borderRadius: "8px",
          border: `1px solid ${BORDER.DEFAULT}`,
          background: BASE.WHITE,
          color: GREY.PRIMARY,
          fontSize: "13px",
          fontWeight: 600,
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "22px",
            height: "22px",
            borderRadius: "50%",
            background: HELP.W_500,
            color: BASE.WHITE,
            fontSize: "10px",
          }}
        >
          ▶
        </span>
        {loading ? "Loading…" : "Play recording"}
      </div>
    );
  }

  const pct = dur > 0 ? (cur / dur) * 100 : 0;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "10px",
        background: BASE.BACKGROUND_LIGHT,
        border: `1px solid ${BORDER.DEFAULT}`,
        borderRadius: "8px",
        padding: "6px 12px",
        maxWidth: "400px",
      }}
    >
      <audio
        ref={audioRef}
        src={url}
        autoPlay
        onLoadedMetadata={onLoadedMeta}
        onTimeUpdate={() => setCur(audioRef.current?.currentTime || 0)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        style={{ display: "none" }}
      />
      <div
        onClick={toggle}
        title={playing ? "Pause" : "Play"}
        style={{
          cursor: "pointer",
          width: "26px",
          height: "26px",
          borderRadius: "50%",
          background: HELP.W_600,
          color: BASE.WHITE,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "13px",
          flex: "0 0 auto",
        }}
      >
        {playing ? "⏸" : "▶"}
      </div>
      <input
        type="range"
        min={0}
        max={dur || 0}
        step="0.1"
        value={cur}
        onChange={onSeek}
        style={{
          flex: 1,
          accentColor: HELP.W_500,
          height: "4px",
          cursor: "pointer",
          background: `linear-gradient(${HELP.W_500},${HELP.W_500}) 0/${pct}% 100% no-repeat ${BORDER.DARK}`,
        }}
      />
      <span
        style={{
          fontSize: "12px",
          fontVariantNumeric: "tabular-nums",
          color: GREY.SECONDARY,
          minWidth: "78px",
          textAlign: "right",
          flex: "0 0 auto",
        }}
      >
        {fmt(cur)} / {fmt(dur)}
      </span>
    </div>
  );
};

export default CallRecordingPlayer;
